// frontend/src/tour/TourProvider.tsx
// React-Context + <TourHost/>-Komponente fuer die Tindra-gefuehrte In-App-Tour.
//
// <TourHost/> MUSS innerhalb von <BrowserRouter> gemountet werden, weil es
// useNavigate + useLocation nutzt. Es kapselt die imperativen Calls der
// tourEngine in einen React-Lifecycle.
//
// Auto-Start-Logik:
//   Part 1: authed + !part1-done + Pfad nicht in EXCLUDED_ROUTES
//           -> ggf. erst auf /dashboard navigieren, dann startPart(1).
//   Part 2: authed + part1-done + !part2-done + path=/dashboard
//           + GET /api/onboarding/coin-status message_count >= MESSAGE_COUNT_THRESHOLD
//           -> startPart(2).
//   Live-Gespraech (7.1.1): authed + part1-done + !live-talk-done
//           + Pfad /chat/* + Anchor "chat-live-talk" im DOM (kurzer Poll)
//           -> Ein-Schritt-Tour auf der aktuellen Chat-Seite, ohne Navigation.
//   Weiter-Kachel (W3-A): authed + part1-done + part2-done + !continue-done
//           + Pfad /dashboard + Anchor "dashboard-continue" im DOM
//           -> Ein-Schritt-Tour auf dem Abenteuer-Tab, ohne Navigation.
//           Die Gates auf part1/part2 halten die bestehende Reihenfolge
//           ein: die Ministrecke laeuft erst, wenn beide Teile durch sind.
//
// localStorage ist Source-of-Truth (NICHT tutorial_campaign_id) — die Tour
// funktioniert dadurch auch in Faellen, in denen die Demo-Kampagne fehlt.
//
// W2-B/N-05 (Befunde G-03, X1-03):
//   • ALLE vier Auto-Starts warten auf useStartupGate() === 'none' — die Tour
//     legt sich nie mehr ueber Rechts-, Profil- oder Push-Dialoge.
//   • „Spaeter" in der Pill und die ZWEITE Pause eines Teils setzen einen
//     gespeicherten Merker (clientPrefs `lorath_tour_part<N>_snoozed`). Danach
//     startet der Teil nicht mehr von selbst; Wiederaufnahme nur noch ueber
//     Einstellungen → Hilfe (startPart setzt den Merker zurueck).
//   • Die Pill erscheint nicht in Chat-Routen und klappt nach 10 s ein.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { favoriteTabOf, favoriteTabPath } from '../auth/postAuthRedirect';
import { apiRequest, ApiError } from '../api/client';
import { assetUrl } from '../api/assets';
import { useT } from '../i18n';
import { useSync } from '../sync/SyncProvider';
import { isBootSplashDone, onBootSplashDone } from '../app/bootSplashSignal';
import { useStartupGate } from '../app/startupGateContext';
import { clearPref, readPref, writePref } from '../shared/clientPrefs';
import {
  buildTourPart1,
  CONTINUE_TILE_STEP,
  LIVE_TALK_STEP,
  MESSAGE_COUNT_THRESHOLD,
  PART_2,
} from './steps';
import {
  ensureTourStyles,
  isTourPillHiddenRoute,
  startTour,
  TOUR_PILL_COLLAPSE_MS,
  type ActiveTour,
  type TourStep,
} from './tourEngine';

const PART1_KEY = 'lorath:tour:part1-done';
const PART2_KEY = 'lorath:tour:part2-done';
const LIVE_TALK_KEY = 'lorath:tour:live-talk-done';
const CONTINUE_TILE_KEY = 'lorath:tour:continue-tile-done';
// W7-B: „erstes Erlebnis" — erste EIGENE Nachricht in einem Charakter-Chat oder
// erster eigener Abenteuer-Zug. Der Coin-Einstieg haengt daran (siehe
// hasFirstExperience unten): niemand soll ueber Coins stolpern, bevor er
// ueberhaupt etwas erlebt hat. Gelesen/geschrieben wird ueber zwei Modul-
// Funktionen — dasselbe Muster wie markTourPart1Skipped, damit auch Seiten
// ausserhalb des TourHost-Kontexts (Chat-Routen) den Merker setzen koennen.
const FIRST_EXPERIENCE_KEY = 'lorath:tour:first-experience';
const TINDRA_AVATAR = assetUrl('/img/tindra.png');

// Kurzer Anchor-Poll fuer die Live-Gespraech-Ministrecke: der Live-Talk-Button
// im ChatHeader mountet ggf. erst nach dem Chat-Load — wir warten kurz auf
// [data-tour-anchor="chat-live-talk"] und geben danach still auf (defensiv:
// existiert der Anchor nicht, z.B. altes Bundle, passiert einfach nichts).
const LIVE_TALK_ANCHOR = 'chat-live-talk';
const LIVE_TALK_POLL_MS = 400;
const LIVE_TALK_POLL_MAX_TRIES = 15; // ~6s
// W3-A: gleiche Mechanik fuer die "Weiter"-Kachel im Abenteuer-Tab.
const CONTINUE_TILE_ANCHOR = 'dashboard-continue';

/**
 * Pfade, auf denen KEIN Auto-Start der Part-1-Tour erfolgen darf: Auth-,
 * Onboarding- und oeffentliche Legal-Routen sowie Arbeitsflaechen, deren
 * Kontext der Autostart nicht durch eine Navigation zum ersten Schritt
 * ersetzen darf. Auf geeigneten authentifizierten Pfaden (z.B. /coins,
 * /dashboard) startet die Tour weiterhin zunaechst auf dem Lieblings-Tab.
 */
const EXCLUDED_ROUTES: readonly RegExp[] = [
  /^\/login(?:\/|$)/,
  /^\/register(?:\/|$)/,
  /^\/onboarding(?:\/|$)/,
  /^\/start(?:\/|$)/,
  /^\/reset-password(?:\/|$)/,
  /^\/legal(?:\/|$)/,
  /^\/kontakt(?:\/|$)/,
  /^\/gruender(?:\/|$)/,
  /^\/settings(?:\/|$)/,
  /^\/world(?:\/|$)/,
  /^\/world-element-edit(?:\/|$)/,
  /^\/world-tree(?:\/|$)/,
  /^\/spielbau(?:\/|$)/,
  // R3/E (Nachpruefung): „Mehr" und die Werkzeug-Seiten sind Ziele, die man
  // bewusst ansteuert (auch aus der fokussierten Leiste der Neukonten). Der
  // Autostart zoege den Neuling per navigate(firstRoute) davon weg. Teil 1
  // startet beim naechsten Routenwechsel auf eine Shell-Seite ganz normal.
  /^\/more(?:\/|$)/,
  /^\/erinnerungen(?:\/|$)/,
  /^\/woche(?:\/|$)/,
  /^\/milestones(?:\/|$)/,
  /^\/soundboard(?:\/|$)/,
  /^\/tindra(?:\/|$)/,
  // W8-C (MAJOR 3): die beiden TUEREN des Gast-Einstiegs. `submitGuest`
  // navigiert nach der Onboarding-Strecke direkt auf /characters/new bzw.
  // /campaign-new; der Auto-Start haette den Gast von dort sofort auf seinen
  // Lieblings-Tab weggezogen — der Begleiter-Bogen bzw. der Abenteuer-Wizard
  // waere weg, bevor das erste Feld ausgefuellt ist. Der Charakter-Editor
  // (auch beim Bearbeiten) ist ein Formular: eine Tour darf ihn nie abreissen.
  //
  // Bewusst NUR die Route ausschliessen und NICHT markTourPart1Skipped()
  // setzen: der Auto-Start setzt `autoStartTriedRef` erst NACH dieser Pruefung,
  // Teil 1 startet also beim naechsten Routenwechsel ganz normal nach.
  /^\/characters\/new(?:\/|$)/,
  /^\/characters\/[^/]+\/edit(?:\/|$)/,
  /^\/campaign-new(?:\/|$)/,
  // W2-B/F-01 (E-06): der Sofortstart der Tueren. Die Tour navigierte beim
  // Start auf den Landeplatz und zoege den Neuling von seiner ersten Wahl weg.
  /^\/first-scene(?:\/|$)/,
  // W2-B: laufende Chats. Ein Tipp auf eine Vorlage oeffnet sofort /chat/:id —
  // Teil 1 darf den Neuling dort nicht aus seiner ersten Szene reissen. Er
  // startet beim naechsten Besuch einer Shell-Seite (Muster MAJOR 3 oben).
  /^\/chat\//,
  /^\/character-chat\//,
  /^\/group-chat\//,
  /^\/table-round-chat\//,
  /^\/table-round-session\//,
];

export function isExcludedRoute(pathname: string): boolean {
  return EXCLUDED_ROUTES.some((pattern) => pattern.test(pathname));
}

interface CoinStatusResponse {
  message_count?: number;
  should_show?: boolean;
}

export interface TourContextValue {
  /** Startet einen Tour-Teil. Loescht das jeweilige done-Flag, damit ein
   *  Replay zuverlaessig startet, und ruft dann die Engine auf. Optionaler
   *  startIndex erlaubt die Wiederaufnahme an der Pausenstelle (CRIT 3). */
  startPart: (part: 1 | 2, startIndex?: number) => void;
  /** Welcher Teil laeuft aktuell (null = nichts). */
  active: 1 | 2 | null;
}

const TourContext = createContext<TourContextValue | null>(null);

export function useTour(): TourContextValue {
  const value = useContext(TourContext);
  if (!value) throw new Error('useTour must be used within TourHost');
  return value;
}

function readDone(key: string): boolean {
  if (typeof localStorage === 'undefined') return false;
  try { return localStorage.getItem(key) === '1'; } catch { return false; }
}

function writeDone(key: string, value: '1' | '0'): void {
  if (typeof localStorage === 'undefined') return;
  try { localStorage.setItem(key, value); } catch { /* ignore */ }
}

function clearDone(key: string): void {
  if (typeof localStorage === 'undefined') return;
  try { localStorage.removeItem(key); } catch { /* ignore */ }
}

// ─── Schlummer-Merker (W2-B/N-05) ─────────────────────────────────────────
// Ueber clientPrefs (Cookie + localStorage, Projektregel fuer neue Client-
// Einstellungen). Je Teil ein Merker und ein Pausenzaehler.
function snoozeKey(part: 1 | 2): string {
  return `lorath_tour_part${part}_snoozed`;
}

function pauseCountKey(part: 1 | 2): string {
  return `lorath_tour_part${part}_pauses`;
}

/** Schlummert dieser Teil (nach „Spaeter" oder der zweiten Pause)? */
export function isTourSnoozed(part: 1 | 2): boolean {
  try { return readPref(snoozeKey(part)) === '1'; } catch { return false; }
}

/** „Spaeter": kein Auto-Start mehr, bis der Teil ueber Hilfe neu startet. */
export function snoozeTour(part: 1 | 2): void {
  try { writePref(snoozeKey(part), '1'); } catch { /* ignore */ }
}

/**
 * Zaehlt eine Pause. Die ZWEITE Pause eines Teils (auch ueber Kaltstarts
 * hinweg) schlummert ihn. Rueckgabe: true, wenn der Teil jetzt schlummert.
 */
export function registerTourPause(part: 1 | 2): boolean {
  let count = 0;
  try { count = Number(readPref(pauseCountKey(part)) ?? 0) || 0; } catch { count = 0; }
  const next = count + 1;
  try { writePref(pauseCountKey(part), String(next)); } catch { /* ignore */ }
  if (next >= 2) {
    snoozeTour(part);
    return true;
  }
  return false;
}

/** Ausdruecklicher Neustart (Einstellungen → Hilfe): Merker und Zaehler weg. */
export function clearTourSnooze(part: 1 | 2): void {
  try {
    clearPref(snoozeKey(part));
    clearPref(pauseCountKey(part));
  } catch { /* ignore */ }
}

/**
 * Startet eine Ein-Schritt-Ministrecke auf der AKTUELLEN Route, sobald ihr
 * Anchor im DOM auftaucht (kurzer Poll — die Ziel-Komponente mountet oft
 * verzoegert). Fehlt der Anchor dauerhaft, passiert still nichts: kein
 * Crash, kein zentriertes Overlay auf einer Seite ohne Ziel (z.B. altes
 * Bundle). Rueckgabewert ist die Aufraeum-Funktion fuer den useEffect.
 *
 * `route` wird mit dem aktuellen Pfad gefuellt, damit die Engine NICHT
 * navigiert (startTour rendert Schritt 1 direkt; Navigation passiert nur bei
 * Route-Mismatch). Abbruch (Skip) und Pause zaehlen wie ein Abschluss —
 * fuer einen einzelnen Schritt ergibt ein Wiederaufnahme-Pill keinen Sinn.
 */
function runAnchoredMiniTour(params: {
  anchor: string;
  step: Omit<TourStep, 'route'>;
  doneKey: string;
  activeRef: { current: ActiveTour | null };
  navigate: (to: string) => void;
  t: (key: string, replacements?: Record<string, string | number>) => string;
}): () => void {
  const { anchor, step, doneKey, activeRef, navigate, t } = params;
  let cancelled = false;
  let tries = 0;
  let timer: number | null = null;

  const finish = () => {
    writeDone(doneKey, '1');
    activeRef.current = null;
  };

  const startIfAnchored = () => {
    if (cancelled) return;
    if (activeRef.current) return; // inzwischen laeuft eine andere Tour
    const element = document.querySelector(`[data-tour-anchor="${anchor}"]`);
    if (!element) {
      tries += 1;
      if (tries >= LIVE_TALK_POLL_MAX_TRIES) return;
      timer = window.setTimeout(startIfAnchored, LIVE_TALK_POLL_MS);
      return;
    }
    const steps: TourStep[] = [{ ...step, route: window.location.pathname }];
    activeRef.current = startTour({
      steps,
      t,
      navigate,
      avatarUrl: TINDRA_AVATAR,
      onComplete: finish,
      onSkip: finish,
      onPause: finish,
    });
  };
  startIfAnchored();

  return () => {
    cancelled = true;
    if (timer != null) window.clearTimeout(timer);
  };
}

/**
 * E5-Rehang (P14): markiert die Dashboard-Tour Part 1 als erledigt, OHNE sie je
 * gezeigt zu haben. Genutzt vom Adoptionspfad: wer eine Probeszene uebernimmt,
 * landet direkt in seiner adoptierten Kampagne (`/chat/:id`) — die Probeszene
 * IST das Tutorial, der Dashboard-Einstieg "oeffne dein erstes Abenteuer" waere
 * redundant. Part 2 ("der Rest der App") bleibt unberuehrt und startet spaeter
 * regulaer auf /dashboard, weil sein Gate `part1-done` nun erfuellt sieht.
 */
export function markTourPart1Skipped(): void {
  writeDone(PART1_KEY, '1');
}

/**
 * W7-B: Hat der Nutzer sein erstes Erlebnis hinter sich? Wahr, sobald er eine
 * eigene Nachricht im Charakter-Chat geschrieben oder einen eigenen Zug im
 * Abenteuer gemacht hat. Der COIN-EINSTIEG (die Tages-Truhe auf dem Abenteuer-
 * Tab, die einzige Coin-Karte, die ein frisches Konto ungefragt sieht) haengt
 * daran und bleibt bis dahin weg.
 *
 * Bewusst localStorage wie die uebrigen Tour-Merker (TourProvider ist hier
 * Source-of-Truth) — es geht um eine Anzeige-Reihenfolge, nicht um Geld.
 */
export function hasFirstExperience(): boolean {
  return readDone(FIRST_EXPERIENCE_KEY);
}

/** Markiert das erste Erlebnis. Idempotent, best-effort, nie werfend. */
export function markFirstExperience(): void {
  writeDone(FIRST_EXPERIENCE_KEY, '1');
}

// ─── W8-C (MAJOR 2): Vorbelegung fuer Bestandskonten ──────────────────────
// FIRST_EXPERIENCE_KEY ist ein NEUER localStorage-Schluessel. Wer die App seit
// Monaten nutzt, hat ihn nicht — und verlor damit die Tages-Truhe auf dem
// Abenteuer-Tab, bis er irgendwo eine Nachricht schrieb (Widerspruch zu E15
// „Bestandsnutzer bleiben full"). Deshalb wird der Merker beim ERSTEN Lauf aus
// bereits vorhandenen Signalen abgeleitet, statt fail-closed leer zu bleiben.
//
// Bewusst OHNE zusaetzlichen Request: alles kommt aus /api/auth/me (das der
// AuthProvider ohnehin haelt) bzw. aus den vorhandenen Tour-Merkern.
// `message_count` gaebe es nur ueber /api/onboarding/coin-status — ein
// Extra-Aufruf fuer eine Anzeige-Reihenfolge waere unverhaeltnismaessig.
const FIRST_EXPERIENCE_PREFILL_KEY = 'lorath:tour:first-experience-prefilled';

function hasTimestamp(value: unknown): boolean {
  return typeof value === 'string' && value.trim() !== '';
}

/**
 * Sieht das Konto nach einem Bestandsnutzer aus? Ein frisches Gastkonto
 * erfuellt KEINE dieser Bedingungen: `onboarding_completed` steht bei der
 * Anlage auf 0 (createGuestUser setzt die Spalte nicht), Tour-Abschluesse gibt
 * es nicht, und der Gast-Einstieg selbst ist juenger als jeder dieser Merker.
 */
export function looksLikeExistingAccount(user: Record<string, unknown> | null | undefined): boolean {
  // Tour-Merker auf DIESEM Geraet — die App wurde hier schon benutzt.
  if (readDone(PART1_KEY) || readDone(PART2_KEY)) return true;
  if (!user) return false;
  // Serverseitige Tour-Abschluesse: aelter als dieses Programm.
  if (hasTimestamp(user.tutorial_tour_completed_at)) return true;
  if (hasTimestamp(user.onboarding_tour_completed_at)) return true;
  if (hasTimestamp(user.character_tour_completed_at)) return true;
  // Registriertes Konto mit abgeschlossenem Onboarding. Der Server setzt das
  // Flag u.a. per Backfill fuer jedes Konto mit Nachrichten bzw. aelter als
  // eine Stunde — ein gerade angelegtes Konto hat es NICHT.
  if (user.is_guest !== true && user.onboarding_completed === true) return true;
  return false;
}

/**
 * hasFirstExperience() mit einmaliger Vorbelegung. Gibt true zurueck, sobald
 * der Coin-Einstieg gezeigt werden darf. Die Auswertung passiert genau einmal
 * (eigener Merker) und wird aufgeschoben, solange /me noch nicht da ist.
 */
export function ensureFirstExperience(user: Record<string, unknown> | null | undefined): boolean {
  if (readDone(FIRST_EXPERIENCE_KEY)) return true;
  if (readDone(FIRST_EXPERIENCE_PREFILL_KEY)) return false;
  if (!user) return false;
  writeDone(FIRST_EXPERIENCE_PREFILL_KEY, '1');
  if (!looksLikeExistingAccount(user)) return false;
  markFirstExperience();
  return true;
}

export function TourHost({ children }: { children: ReactNode }): ReactNode {
  const navigate = useNavigate();
  const location = useLocation();
  const { status, user } = useAuth();
  // W2-B/N-05: Kein Auto-Start, solange ein Startup-Gate offen ist (Rechte,
  // Profil, Push-Frage). Muster CrossOnboardingHost („Nie ueber einem Gate").
  const startupGate = useStartupGate();
  const gateOpen = startupGate !== 'none';
  // W7-B: Die Tuer bestimmt die Reihenfolge von Teil 1 und den Landeplatz, auf
  // dem Teil 2 anspringt. Wahrheit ist der Server (`favorite_tab` aus /me);
  // favoriteTabOf faellt auf den clientPrefs-Spiegel bzw. 'adventures' zurueck.
  const favoriteTab = favoriteTabOf(user);
  const homeRoute = favoriteTabPath(user);
  // SyncProvider liegt in App.tsx UEBER TourHost — useSync ist hier verfuegbar.
  const { status: syncStatus } = useSync();
  // useT() muss vorhanden sein — I18nProvider wird in App.tsx ueber
  // BrowserRouter/TourHost gemountet.
  const { t } = useT();
  const [active, setActive] = useState<1 | 2 | null>(null);
  const activeRef = useRef<ActiveTour | null>(null);
  // CRIT 3: Auto-Start von Part 1 erst NACH dem Vorspann. Das Latch wird beim
  // BootSplash-Unmount (bzw. Session-Skip) gesetzt.
  const [splashDone, setSplashDone] = useState<boolean>(() => isBootSplashDone());
  // CRIT 3: pausierte Tour (Klick daneben / Esc). null = nichts pausiert. Merkt
  // sich Teil + Schritt-Index, damit die Wiederaufnahme genau dort ansetzt.
  const [paused, setPaused] = useState<{ part: 1 | 2; index: number } | null>(null);
  // W2-B/N-05: Pill nach TOUR_PILL_COLLAPSE_MS einklappen; ein Tipp auf das
  // eingeklappte Symbol klappt sie wieder auf (und startet die Frist neu).
  const [pillCollapsed, setPillCollapsed] = useState(false);
  const [pillExpandNonce, setPillExpandNonce] = useState(0);
  // Schutz: Auto-Start nur EINMAL pro Mount.
  const autoStartTriedRef = useRef(false);
  // Schutz, dass Part 2 nur einmal pro Session probiert wird, auch wenn
  // message_count noch nicht erreicht ist (kein Spam-Refetch).
  const part2ProbeTriedRef = useRef(false);
  // Schutz, dass die Live-Gespraech-Ministrecke nur einmal pro Session
  // probiert wird (kein Poll-Spam bei jedem Chat-Wechsel).
  const liveTalkTriedRef = useRef(false);
  // Dasselbe fuer die Wiedereinstiegs-Ministrecke auf dem Abenteuer-Tab.
  const continueTileTriedRef = useRef(false);

  const stopActive = useCallback(() => {
    if (activeRef.current) {
      try { activeRef.current.stop(); } catch { /* ignore */ }
      activeRef.current = null;
    }
    setActive(null);
  }, []);

  // Cleanup beim Unmount, damit kein Overlay zurueckbleibt.
  useEffect(() => () => stopActive(), [stopActive]);

  // CRIT 3: auf das "Vorspann durch"-Signal warten (feuert sofort, falls schon
  // durch — z.B. Web-Session-Skip).
  useEffect(() => onBootSplashDone(() => setSplashDone(true)), []);

  // Interner Start. `resetSnooze` nur beim ausdruecklichen Neustart ueber die
  // Hilfe (startPart) — Auto-Start und Pill-Wiederaufnahme lassen den
  // Pausenzaehler stehen, sonst zaehlte die „zweite Pause" nie.
  const launchPart = useCallback((part: 1 | 2, startIndex = 0, resetSnooze = false) => {
    if (activeRef.current) return; // laeuft schon
    if (resetSnooze) clearTourSnooze(part);
    const steps: TourStep[] = part === 1 ? buildTourPart1(favoriteTab) : PART_2;
    if (!steps || steps.length === 0) return;
    const doneKey = part === 1 ? PART1_KEY : PART2_KEY;
    // Replay: done-Flag loeschen, damit die naechste Login-Session die Tour
    // NICHT erneut automatisch startet, bis sie manuell wieder angestossen wird.
    clearDone(doneKey);
    // Eine neue oder wiederaufgenommene Tour hebt eine offene Pause auf.
    setPaused(null);
    const clampedStart = Math.min(Math.max(0, startIndex), steps.length - 1);
    // Beim Replay/Resume stehen wir evtl. nicht auf der Route des Startschritts
    // → zuerst dorthin navigieren, damit der Schritt seinen Anchor findet statt
    // zentriert zu erscheinen. Beim Auto-Start (bereits auf /dashboard) No-op.
    const firstRoute = steps[clampedStart]?.route;
    if (firstRoute && typeof window !== 'undefined' && window.location.pathname !== firstRoute) {
      try { navigate(firstRoute); } catch { /* ignore */ }
    }
    const engine = startTour({
      steps,
      t,
      navigate,
      avatarUrl: TINDRA_AVATAR,
      startIndex: clampedStart,
      onComplete: () => {
        writeDone(doneKey, '1');
        activeRef.current = null;
        setActive(null);
        setPaused(null);
      },
      onSkip: () => {
        // Expliziter Skip (der "Ueberspringen"-Button in der Bubble) = als
        // erledigt werten, damit die Tour nicht bei jedem Reload wieder
        // einblendet. NUR das setzt das done-Flag.
        writeDone(doneKey, '1');
        if (part === 1) writeDone(PART2_KEY, '1');
        writeDone(LIVE_TALK_KEY, '1');
        writeDone(CONTINUE_TILE_KEY, '1');
        activeRef.current = null;
        setActive(null);
        setPaused(null);
      },
      onPause: (index) => {
        // Pause (Klick daneben / Esc): KEIN done-Flag. Wir merken uns Teil +
        // Schritt und bieten die Wiederaufnahme an (CRIT 3). W2-B/N-05: die
        // zweite Pause eines Teils schlummert ihn dauerhaft (kein Auto-Start
        // bei jedem Kaltstart mehr); die Pill bleibt fuer diese Sitzung.
        activeRef.current = null;
        setActive(null);
        registerTourPause(part);
        setPaused({ part, index });
      },
    });
    activeRef.current = engine;
    setActive(part);
  }, [favoriteTab, navigate, t]);

  /** Oeffentlicher Start (Einstellungen → Hilfe): setzt den Schlummer zurueck. */
  const startPart = useCallback((part: 1 | 2, startIndex = 0) => {
    launchPart(part, startIndex, true);
  }, [launchPart]);

  const value = useMemo<TourContextValue>(() => ({ startPart, active }), [startPart, active]);

  // ─── Auto-Start Part 1 ────────────────────────────────────────────────
  // Erlaubt jetzt auch Pfade ausserhalb /dashboard, solange sie nicht in
  // EXCLUDED_ROUTES stehen (Auth/Onboarding). Der Provider navigiert vorher
  // auf die erste Step-Route (/dashboard), damit die Spotlight-Anchors
  // dort gefunden werden.
  useEffect(() => {
    if (status !== 'authed') return;
    if (typeof window === 'undefined') return;
    if (gateOpen) return; // W2-B/N-05: nie ueber einem Startup-Gate
    if (isExcludedRoute(location.pathname) || location.pathname !== homeRoute) return;
    // CRIT 3: erst NACH dem Vorspann UND wenn der Erststart-Bootstrap
    // (ContentSetup) durch ist. Sonst rendert die Tour ihr Overlay unter dem
    // Siegel bzw. zeigt auf Anchor, deren Seite noch laedt. Beide Signale sind
    // in den Effekt-Deps → sobald sie kippen, greift der Auto-Start.
    if (!splashDone) return;
    if (syncStatus === 'bootstrapping') return;
    if (readDone(PART1_KEY)) return;
    if (isTourSnoozed(1)) return; // W2-B/N-05: „Spaeter" / zweite Pause
    if (activeRef.current) return;
    if (autoStartTriedRef.current) return;
    autoStartTriedRef.current = true;
    launchPart(1);
  }, [gateOpen, homeRoute, launchPart, location.pathname, status, splashDone, syncStatus]);

  // ─── Auto-Start Part 2 (Aktivitaets-Gate) ─────────────────────────────
  useEffect(() => {
    if (status !== 'authed') return;
    if (gateOpen) return; // W2-B/N-05
    // W7-B: Teil 2 springt auf dem Landeplatz der Tuer an, nicht mehr hart auf
    // /dashboard — sonst bekaeme ein Chats-Nutzer Teil 2 nie zu sehen. Fuer
    // 'adventures' ist homeRoute exakt '/dashboard' (unveraendert).
    if (location.pathname !== homeRoute) return;
    if (readDone(PART2_KEY)) return;
    if (!readDone(PART1_KEY)) return; // Part 2 erst NACH Part 1
    if (isTourSnoozed(2)) return; // W2-B/N-05
    if (activeRef.current) return;
    if (part2ProbeTriedRef.current) return;
    part2ProbeTriedRef.current = true;

    let cancelled = false;
    void (async () => {
      try {
        const data = await apiRequest<CoinStatusResponse>('/api/onboarding/coin-status', {
          // Kurzer Timeout — wir wollen den Dashboard-Render nicht aufhalten.
          timeoutMs: 6000,
        });
        if (cancelled) return;
        const count = typeof data?.message_count === 'number' ? data.message_count : 0;
        if (count >= MESSAGE_COUNT_THRESHOLD) {
          launchPart(2);
        }
      } catch (err) {
        // Fehler tolerant schlucken — Offline oder 401 (im guest-Pfad bereits
        // oben abgefangen, aber 5xx/Netzwerk soll die Tour-UX nicht blockieren).
        if (!(err instanceof ApiError && err.status === 401)) {
          // stiller Fallback: Tour laeuft erst beim naechsten Dashboard-Besuch.
        }
      }
    })();

    return () => { cancelled = true; };
  }, [gateOpen, homeRoute, launchPart, location.pathname, status]);

  // ─── Auto-Start Live-Gespraech-Ministrecke (7.1.1) ────────────────────
  // Analog Part 2, aber auf der AKTUELLEN Chat-Seite (/chat/:id) und ohne
  // Navigation: `route` wird mit dem aktuellen Pfad gefuellt, damit die
  // Engine keinen Route-Wechsel ausloest (startTour rendert Schritt 1
  // direkt; navigiert wird nur bei Route-Mismatch). Bedingungen:
  //   (a) authed, (b) Pfad matcht /^\/chat\//, (c) live-talk-done nicht
  //   gesetzt, (d) Part 1 erledigt (kein Tour-Stacking im Erst-Onboarding),
  //   (e) keine andere Tour aktiv, (f) der Anchor [data-tour-anchor=
  //   "chat-live-talk"] existiert im DOM (kurzer Poll, der ChatHeader
  //   mountet ggf. verzoegert; fehlt er dauerhaft → still aufgeben).
  useEffect(() => {
    if (status !== 'authed') return;
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    if (gateOpen) return; // W2-B/N-05
    if (!location.pathname.startsWith('/chat/')) return;
    if (readDone(LIVE_TALK_KEY)) return;
    if (!readDone(PART1_KEY)) return; // erst NACH dem Erst-Onboarding
    // W2-B/F-01: Nicht in der allerersten Szene. Nach einem Sofortstart legte
    // sich der Hinweis auf das (bezahlte) Live-Gespraech sonst ueber die
    // Eroeffnung, bevor der Neuling ein Wort geschrieben hatte (im Browser
    // nachgestellt, 23.09.2026). Erst nach dem ersten eigenen Zug — dieselbe
    // Regel wie beim Coin-Einstieg (W7-B).
    if (!hasFirstExperience()) return;
    if (activeRef.current) return;
    if (liveTalkTriedRef.current) return;
    liveTalkTriedRef.current = true;

    return runAnchoredMiniTour({
      anchor: LIVE_TALK_ANCHOR,
      step: LIVE_TALK_STEP,
      doneKey: LIVE_TALK_KEY,
      activeRef,
      navigate,
      t,
    });
  }, [gateOpen, location.pathname, navigate, status, t]);

  // ─── Auto-Start Wiedereinstiegs-Ministrecke (W3-A) ────────────────────
  // Gleiche Mechanik wie oben, Ziel ist die "Weiter"-Kachel im Abenteuer-Tab
  // (Anchor `dashboard-continue`). Zusaetzlich zum Part-1-Gate verlangen wir
  // hier auch part2-done: Part 2 startet selbst auf /dashboard, und die
  // Ministrecke darf sich nicht dazwischendraengen — die bestehende
  // Part-1/Part-2-Reihenfolge bleibt damit unangetastet.
  useEffect(() => {
    if (status !== 'authed') return;
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    if (gateOpen) return; // W2-B/N-05
    if (!location.pathname.startsWith('/dashboard')) return;
    if (readDone(CONTINUE_TILE_KEY)) return;
    if (!readDone(PART1_KEY)) return;
    if (!readDone(PART2_KEY)) return;
    if (activeRef.current) return;
    if (continueTileTriedRef.current) return;
    continueTileTriedRef.current = true;

    return runAnchoredMiniTour({
      anchor: CONTINUE_TILE_ANCHOR,
      step: CONTINUE_TILE_STEP,
      doneKey: CONTINUE_TILE_KEY,
      activeRef,
      navigate,
      t,
    });
  }, [gateOpen, location.pathname, navigate, status, t]);

  // Sobald etwas pausiert ist, die Tour-Styles sicherstellen (das Pill nutzt
  // dieselben injizierten Klassen; nach einer Pause ist das <style> ohnehin da).
  useEffect(() => {
    if (paused) ensureTourStyles();
  }, [paused]);

  // W2-B/N-05: nach TOUR_PILL_COLLAPSE_MS einklappen. Neu gestartet bei jeder
  // neuen Pause und bei jedem Aufklappen per Tipp.
  useEffect(() => {
    setPillCollapsed(false);
    if (!paused) return undefined;
    const timer = window.setTimeout(() => setPillCollapsed(true), TOUR_PILL_COLLAPSE_MS);
    return () => window.clearTimeout(timer);
  }, [paused, pillExpandNonce]);

  const pillHidden = isTourPillHiddenRoute(location.pathname);

  return (
    <TourContext.Provider value={value}>
      {children}
      {paused && !pillHidden && pillCollapsed ? (
        <button
          type="button"
          className="lorath-tour-resume is-collapsed"
          aria-label={t('tour.resume.title')}
          title={t('tour.resume.title')}
          data-testid="tour-resume-collapsed"
          onClick={() => setPillExpandNonce((n) => n + 1)}
        >
          <img
            className="lorath-tour-resume-avatar"
            src={TINDRA_AVATAR}
            alt=""
            aria-hidden="true"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
          />
        </button>
      ) : null}
      {paused && !pillHidden && !pillCollapsed ? (
        <div
          className="lorath-tour-resume"
          role="dialog"
          aria-live="polite"
          aria-label={t('tour.resume.title')}
          data-testid="tour-resume"
        >
          <img
            className="lorath-tour-resume-avatar"
            src={TINDRA_AVATAR}
            alt=""
            aria-hidden="true"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
          />
          <span className="lorath-tour-resume-text">{t('tour.resume.title')}</span>
          <span className="lorath-tour-resume-actions">
            <button
              type="button"
              className="lorath-tour-resume-btn"
              // W2-B/N-05: „Spaeter" merkt sich die Entscheidung (clientPrefs) —
              // vorher setzte es nur den React-Zustand zurueck und Teil 1 kam
              // beim naechsten Kaltstart wieder von vorn.
              onClick={() => { snoozeTour(paused.part); setPaused(null); }}
            >
              {t('tour.resume.dismiss')}
            </button>
            <button
              type="button"
              className="lorath-tour-resume-btn primary"
              onClick={() => { const p = paused; setPaused(null); launchPart(p.part, p.index); }}
            >
              {t('tour.resume.cta')}
            </button>
          </span>
        </div>
      ) : null}
    </TourContext.Provider>
  );
}
